import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown'
import { OverlayPanel } from 'primereact/overlaypanel'
import { FormEvent, Nullable } from 'primereact/ts-helpers'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import {
  TParamFilterDateRange,
  TParamFilterSelect,
  TParamFilterData,
} from './PrimeTableCustomHeader'

const DateRangeFilter = (filterDetails: TParamFilterDateRange) => {
  const [dates, setDates] = useState<Nullable<(Date | null)[]>>()
  const { t } = useTranslation()
  const { search } = useLocation()
  const history = useHistory()
  const urlSearchParams = useMemo(() => new URLSearchParams(search), [search])

  const changeDateRange = (e: FormEvent<(Date | null)[]>) => {
    if (e.value && e.value.every(date => date !== null)) {
      const formatDateRange = e.value
        .map(date => {
          return moment(date).format('YYYY-MM-DD HH:mm:ss')
        })
        .join(', ')
      urlSearchParams.set(filterDetails.name, `[${formatDateRange}]`)
      urlSearchParams.set('offset', '0')
      const newSearchParam = urlSearchParams.toString().replace(/\+/g, ' ')
      history.push({
        pathname: window.location.pathname,
        search: decodeURIComponent(newSearchParam),
      })
    }
    setDates(e.value)
    return e
  }

  useEffect(() => {
    if (!filterDetails.value) {
      setDates(undefined)
    } else {
      setDates([
        new Date(filterDetails.value.from),
        new Date(filterDetails.value.to),
      ])
    }
  }, [filterDetails.value, search])

  const removeDateRange = () => {
    urlSearchParams.delete(filterDetails.name)
    urlSearchParams.set('offset', '0')
    const newSearchParam = urlSearchParams.toString()
    history.push({
      pathname: window.location.pathname,
      search: decodeURIComponent(newSearchParam),
    })
    setDates(undefined)
  }

  return (
    <div key={filterDetails.name}>
      <label htmlFor="buttondisplay">
        {t(filterDetails.label || filterDetails.name)}
      </label>
      <div>
        <Calendar
          value={dates}
          onChange={e => changeDateRange(e)}
          showIcon
          selectionMode="range"
          readOnlyInput
        />
        {filterDetails.value ? (
          <FontAwesomeIcon onClick={() => removeDateRange()} icon={'xmark'} />
        ) : null}
      </div>
    </div>
  )
}

const SelectFilter = (filterDetails: TParamFilterSelect) => {
  const [selectedOption, setSelectedOption] = useState<string>(
    filterDetails.value ? filterDetails.value : '',
  )
  const { t } = useTranslation()
  const { search } = useLocation()
  const history = useHistory()
  const urlSearchParams = useMemo(() => new URLSearchParams(search), [search])

  const selectChange = (event: DropdownChangeEvent) => {
    setSelectedOption(event.value)

    urlSearchParams.set(filterDetails.name, event.value)
    urlSearchParams.set('offset', '0')
    const newSearchParam = urlSearchParams.toString()
    history.push({
      pathname: window.location.pathname,
      search: decodeURIComponent(newSearchParam),
    })
  }

  useEffect(() => {
    setSelectedOption(filterDetails.value ?? '')
  }, [filterDetails.value, search])

  if (!filterDetails.list) return null

  return (
    <div key={filterDetails.name}>
      <label htmlFor="buttondisplay" className="font-bold block mb-2">
        {t(filterDetails.label || filterDetails.name)}
      </label>
      <Dropdown
        value={selectedOption}
        onChange={e => selectChange(e)}
        options={filterDetails.list}
        placeholder={t('Select a subscription')}
        itemTemplate={option => {
          return <li>{option.label}</li>
        }}
      />
    </div>
  )
}

const FilterSwitch = (filterDetails: TParamFilterData) => {
  const { t } = useTranslation()
  if (filterDetails.type === 'date-range') {
    return DateRangeFilter(filterDetails)
  } else if (filterDetails.type === 'select') {
    return SelectFilter(filterDetails)
  }
  return <p>{t('This type is not supported')}</p>
}

type TProps = { apiFilterArray: TParamFilterData[] }

export const PrimeTableCustomFilterSelect = ({ apiFilterArray }: TProps) => {
  const op = useRef<OverlayPanel>(null)

  const { t } = useTranslation()

  return (
    <>
      <Button onClick={e => op.current?.toggle(e)}>
        <FontAwesomeIcon icon={'filter'} />
        {t('Filter')}
      </Button>
      <OverlayPanel ref={op} closeOnEscape>
        {apiFilterArray.map(apiFilter => FilterSwitch(apiFilter))}
      </OverlayPanel>
    </>
  )
}
